import React, { useState, useEffect } from 'react';
import Layout from '../component/layout'
import { Link, useNavigate } from 'react-router-dom'
import { useParams } from 'react-router-dom';
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact";
import { toastify } from '../../../utils/toast';
import { makeRequest2 } from '../../../utils/makeRequest';
import { getItem } from '../../../utils/localStorage';
import { getTicketURL, approveComplaintURL, getUsersURL, getVasVendors, assignTicketURL, cancelTicketURL, reopenTicketURL, approveReviewURL } from '../../../utils/urls';

const ViewTicket = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [isReviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [reviewText, setReviewText] = useState('');
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [fullScreenImage, setFullScreenImage] = useState('');
  const [byWho, setByWho] = useState('');
  const [selectedVendor, setSelectedVendor] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [serviceVendors, setServiceVendors] = useState([]);
  const [users, setUsers] = useState([]);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [reopenReason, setReopenReason] = useState('');

  const fetchServiceVendors = async () => {
    try {
      const facilityId = await getItem('selectedFacilityId');
      const url = `${getVasVendors}/${facilityId}`;
      const response = await makeRequest2(url, 'GET', {});

      if (response?.success) {
        if (Array.isArray(response.data)) {
          setServiceVendors(response.data);
          if (response.data.length === 0) {
            toastify('No vendors available', 'info');
          }
        } else if (Array.isArray(response)) {
          setServiceVendors(response);
          if (response.length === 0) {
            toastify('No vendors available', 'info');
          }
        }
      } else {
        setServiceVendors([]);
        toastify('Error loading vendors', 'error');
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
      setServiceVendors([]);
      toastify('Error fetching vendors', 'error');
    }
  };

  const getCurrentUser = async () => {
    try {
      const currentUserValue = await getItem("APPUSER");

      let currentUser;
      // Check if the value needs to be parsed
      if (typeof currentUserValue === "string") {
        try {
          currentUser = JSON.parse(currentUserValue);
        } catch (err) {
          console.error("Error parsing APPUSER:", err);
        }
      } else {
        currentUser = currentUserValue;
      }

      if (currentUser?.user) {
        return currentUser; // Return the entire object, not just phoneNumber
      } else {
        console.error("Invalid user structure:", currentUser);
        return null;
      }
    } catch (err) {
      console.error(err.message);
      return null;
    }
  };

  const fetchUsers = async () => {
    const facilityId = await getItem('selectedFacilityId');
    try {
      const response = await makeRequest2(`${getUsersURL}/${facilityId}`, 'GET', {});

      if (response.success) {
        const filteredUsers = response.data.filter(user => user.department?.name === 'Tickets');
        setUsers(filteredUsers);
      } else {
        throw new Error(response.error);
      }
    } catch (err) {
      toastify(err.message, 'error');
    }
  };

  const handleAssignForAssessment = async () => {
    try {
      const facilityId = await getItem('selectedFacilityId');
      const currentUserData = await getCurrentUser();

      // Find the selected user/vendor details including phone numbers
      let selectedEmployeeDetails = null;
      let selectedVendorDetails = null;
      let actualByWho = byWho;

      if (byWho === "propertyManager" && selectedEmployee) {
        const user = users.find(u => u._id === selectedEmployee);
        if (user) {
          selectedEmployeeDetails = {
            id: user._id,
            fullName: user.fullName,
            phoneNumber: user.phoneNumber,
            email: user.email
          };
        }
      } else if (byWho === "vendor" && selectedVendor) {
        selectedVendorDetails = {
          id: selectedVendor._id,
          fullName: selectedVendor.fullName,
          phoneNumber: selectedVendor.phoneNumber,
          email: selectedVendor.email
        };
      } else if (byWho === "self") {
        // Assign to current logged-in user (Property Manager)
        actualByWho = "propertyManager";
        selectedEmployeeDetails = {
          id: currentUserData.user.userId,
          fullName: currentUserData.user.fullName,
          phoneNumber: currentUserData.user.phoneNumber,
          email: currentUserData.user.email
        };
      }

      const payload = {
        byWho: actualByWho,
        selectedEmployee: actualByWho === "propertyManager" ? selectedEmployeeDetails : null,
        selectedVendor: actualByWho === "vendor" ? selectedVendorDetails : null,
        status: 'assigned'
      };

      const response = await makeRequest2(`${assignTicketURL}/${facilityId}/${ticketId}`, 'POST', payload);

      if (response.success) {
        toastify("Ticket Assigned successfully", 'success');
        getTicket();
      } else {
        throw new Error(response.error);
      }
    } catch (err) {
      toastify(err.message, 'error');
    }
  };

  const handleApproveReview = async () => {
    try {
      const facilityId = await getItem('selectedFacilityId');
      // Get the user ID from the current user data
      const userId = await getItem('userId');

      const payload = {
        approvedBy: userId
      };

      const response = await makeRequest2(`${approveReviewURL}/${facilityId}/${ticketId}`, 'POST', payload);
      if (response.success) {
        toastify("Ticket Approved successfully", 'success');
        getTicket();
      } else {
        throw new Error(response.error);
      }
    } catch (err) {
      toastify(err.message, 'error');
    }
  }

  const calculateOngoingDuration = (ticket) => {
    if (ticket.status !== 'ongoing' || !ticket.ongoingSince) {
      return 'Not started';
    }

    const now = new Date();
    const ongoingSince = new Date(ticket.ongoingSince);
    const totalDuration = ticket.totalOngoingDuration || 0;

    // Calculate current session duration
    const currentSession = now.getTime() - ongoingSince.getTime();
    const totalMilliseconds = totalDuration + currentSession;

    // Convert to readable format
    const days = Math.floor(totalMilliseconds / (1000 * 60 * 60 * 24));
    const hours = Math.floor((totalMilliseconds % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((totalMilliseconds % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  const handleCancelTicket = async () => {
    try {
      const facilityId = await getItem('selectedFacilityId');
      const userId = await getItem('userId');
      const response = await makeRequest2(
        `${cancelTicketURL}/${facilityId}/${ticketId}`,
        'POST',
        { cancellationReason: cancelReason, cancelledBy: userId }
      );

      console.log("Cancel Ticket Response:", response);

      if (response.success) {
        toastify('Ticket cancelled successfully', 'success');
        setShowCancelModal(false);
        setCancelReason('');
        getTicket();
      } else {
        throw new Error(response.error);
      }
    } catch (err) {
      toastify(err.message, 'error');
    }
  };

  const handleReopenTicket = async () => {
    try {
      const facilityId = await getItem('selectedFacilityId');
      const userId = await getItem('userId');
      const response = await makeRequest2(
        `${reopenTicketURL}/${facilityId}/${ticketId}`,
        'POST',
        { reopenedReason: reopenReason, reopenedBy: userId }
      );

      if (response.success) {
        toastify('Ticket reopened successfully', 'success');
        setShowReopenModal(false);
        setReopenReason('');
        getTicket();
      } else {
        throw new Error(response.error);
      }
    } catch (err) {
      toastify(err.message, 'error');
    }
  };

  const openFullScreen = (imageSrc) => {
    setIsFullScreen(true);
    setFullScreenImage(imageSrc);
  };

  const closeFullScreen = () => {
    setIsFullScreen(false);
  };

  const getTicket = async () => {
    try {
      const facilityId = await getItem('selectedFacilityId');
      const response = await makeRequest2(`${getTicketURL}/${facilityId}/${ticketId}`, 'GET', {});

      if (response.success) {
        const fetchedTicket = response.data.ticket;
        // Attach RequesterInfo to the ticket
        const ticketWithRequesterInfo = {
          ...fetchedTicket,
          fullName: fetchedTicket.RequesterInfo?.fullName || 'Unknown',
          phoneNumber: fetchedTicket.RequesterInfo?.phoneNumber || '',
          email: fetchedTicket.RequesterInfo?.email || '',
          requester: fetchedTicket.RequesterInfo?.requester || '',
          assignedEmployeeInfo: fetchedTicket.assignedEmployeeInfo,
          assignedVendorInfo: fetchedTicket.assignedVendorInfo,
          cancelledByInfo: fetchedTicket.cancelledByInfo,
          reopenedByInfo: fetchedTicket.reopenedByInfo
        };
        setTicket(ticketWithRequesterInfo);
      } else {
        throw new Error(response.message || 'Failed to fetch ticket details');
      }
    } catch (err) {
      toastify(err.message, 'error');
    }
  };

  const handleSubmitReview = async (ticketId, reviewText) => {
    try {
      const facilityId = await getItem('selectedFacilityId');
      if (!reviewText) {
        toastify('Please enter a review', 'error');
        return
      }
      const response = await makeRequest2(`${approveComplaintURL}/${facilityId}/${ticketId}`, 'POST', {
        review: reviewText,
      });

      if (response.success) {
        toastify("Review sent successfully", 'success');
        setReviewDialogOpen(false);
      } else {
        toastify(response.error, 'error');
      }
    } catch (err) {
      toastify('Failed to send review', 'error');
    }
  };

  const handleAction = (ticket) => {
    setSelectedTicket(ticket);
    setReviewDialogOpen(true);
  };

  useEffect(() => {
    getTicket();
    getCurrentUser();
    fetchUsers();
    fetchServiceVendors();
  }, [])

  if (!ticket) {
    return <div>Loading...</div>;
  }

  return (
    <Layout>
      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to={"/facility/"}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={"facility/ticket_management/tickets"}>Tickets</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={"#"}>View Ticket</Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-sm-12">
          <div className="row">
            <div className="col-sm-12">
              <div className="card">
                <div className="card-header">
                  <Link to={"#"} onClick={() => navigate(-1)}><span><i className="ti ti-arrow-narrow-left"></i> Back</span></Link>
                </div>
              </div>

              <div className="card border-0">
                <div className="card-body p-0">
                  <div className="card-header bg-light border-bottom text-center py-3">
                    <h4 className="mb-0 text-dark">
                      <i className="fas fa-ticket-alt me-2 text-primary"></i>Ticket Details
                    </h4>
                  </div>

                  <div className="card-body p-4">
                    <div className="row">
                      <div className="col-md-6 mb-4">
                        <label className="form-label text-muted mb-1" style={{ fontSize: '1rem' }}>Raised By</label>
                        <div className="d-flex align-items-center">
                          <p className="mb-0 fs-6 text-dark">{ticket.fullName}</p>
                          {ticket.isStaff && (
                            <span className="badge bg-primary ms-2 fs-7">Staff</span>
                          )}
                        </div>
                      </div>

                      <div className="col-md-6 mb-4">
                        <label className="form-label text-muted mb-1" style={{ fontSize: '1rem' }}>Phone Number</label>
                        <p className="mb-0 fs-6 text-dark">
                          {ticket.phoneNumber ? `${ticket.phoneNumber}` : 'Not provided'}
                        </p>
                      </div>

                      <div className="col-md-6 mb-4">
                        <label className="form-label text-muted mb-1" style={{ fontSize: '1rem' }}>Type</label>
                        <p className="mb-0 fs-6 text-dark">{ticket.ticketType}</p>
                      </div>

                      <div className="col-md-6 mb-4">
                        <label className="form-label text-muted mb-1" style={{ fontSize: '1rem' }}>Subject</label>
                        <p className="mb-0 fs-6 text-dark">{ticket.subject}</p>
                      </div>

                      <div className="col-md-6 mb-4">
                        <label className="form-label text-muted mb-1" style={{ fontSize: '1rem' }}>Priority</label>
                        <p className="mb-0 fs-6 text-dark">{ticket.priority || 'Not set'}</p>
                      </div>

                      <div className="col-md-6 mb-4">
                        <label className="form-label text-muted mb-1" style={{ fontSize: '1rem' }}>Date Created</label>
                        <p className="mb-0 fs-6 text-dark">
                          {new Date(ticket.date).toLocaleString()}
                        </p>
                      </div>

                      <div className="col-12 mb-4">
                        <label className="form-label text-muted mb-1" style={{ fontSize: '1rem' }}>Status</label>
                        <p className="mb-0 fs-6 text-dark" style={{ textAlign: 'justify', lineHeight: '1.5' }}>
                          {ticket.status}
                        </p>
                      </div>

                      {ticket.status === 'ongoing' && (
                        <div className="col-md-6 mb-4">
                          <label className="form-label text-muted mb-1" style={{ fontSize: '1rem' }}>Time in Progress</label>
                          <div className="d-flex align-items-center">
                            <i className="fas fa-clock me-2 text-warning"></i>
                            <p className="mb-0 fs-6 text-dark">
                              {calculateOngoingDuration(ticket)}
                            </p>
                          </div>
                        </div>
                      )}

                      {ticket.reopenCount > 0 && (
                        <div className="col-md-6 mb-4">
                          <label className="form-label text-muted mb-1" style={{ fontSize: '1rem' }}>Times Reopened</label>
                          <div className="d-flex align-items-center">
                            <span className="badge bg-warning text-dark me-2">{ticket.reopenCount}</span>
                            <p className="mb-0 fs-6 text-dark">time(s)</p>
                          </div>
                        </div>
                      )}

                      {ticket.status === 'cancelled' && ticket.cancellationReason && (
                        <div className="col-12 mb-4">
                          <label className="form-label text-muted mb-1" style={{ fontSize: '1rem' }}>Cancellation Reason</label>
                          <div className="alert alert-light border">
                            <div className="d-flex align-items-start">
                              <i className="fas fa-ban text-danger me-2 mt-1"></i>
                              <div>
                                <p className="mb-1 text-dark">{ticket.cancellationReason}</p>
                                {ticket.cancelledAt && (
                                  <small className="text-muted">
                                    Cancelled on {new Date(ticket.cancelledAt).toLocaleString()}
                                  </small>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {ticket.reopenedReason && (
                        <div className="col-12 mb-4">
                          <label className="form-label text-muted mb-1" style={{ fontSize: '1rem' }}>Reopened Reason</label>
                          <div className="alert alert-light border">
                            <div className="d-flex align-items-start">
                              <i className="fas fa-redo text-success me-2 mt-1"></i>
                              <div>
                                <p className="mb-1 text-dark">{ticket.reopenedReason}</p>
                                {ticket.reopenedAt && (
                                  <small className="text-muted">
                                    Reopened on {new Date(ticket.reopenedAt).toLocaleString()}
                                  </small>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {ticket.status === 'cancelled' && ticket.cancelledBy && (
                        <div className="col-md-6 mb-4">
                          <label className="form-label text-muted mb-1" style={{ fontSize: '1rem' }}>Cancelled By</label>
                          <div className="d-flex align-items-center">
                            <i className="fas fa-user-times me-2 text-danger"></i>
                            <div>
                              <p className="mb-1 fs-6 text-dark fw-medium">
                                {ticket.cancelledByInfo?.fullName || 'User'}
                              </p>
                              {ticket.cancelledByInfo?.phoneNumber && (
                                <p className="mb-0 text-muted small">
                                  <i className="fas fa-phone me-1"></i>
                                  {ticket.cancelledByInfo.phoneNumber}
                                </p>
                              )}
                              {ticket.cancelledByInfo?.email && (
                                <p className="mb-0 text-muted small">
                                  <i className="fas fa-envelope me-1"></i>
                                  {ticket.cancelledByInfo.email}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {ticket.reopenedBy && (
                        <div className="col-md-6 mb-4">
                          <label className="form-label text-muted mb-1" style={{ fontSize: '1rem' }}>Reopened By</label>
                          <div className="d-flex align-items-center">
                            <i className="fas fa-user-check me-2 text-success"></i>
                            <div>
                              <p className="mb-1 fs-6 text-dark fw-medium">
                                {ticket.reopenedByInfo?.fullName || 'User'}
                              </p>
                              {ticket.reopenedByInfo?.phoneNumber && (
                                <p className="mb-0 text-muted small">
                                  <i className="fas fa-phone me-1"></i>
                                  {ticket.reopenedByInfo.phoneNumber}
                                </p>
                              )}
                              {ticket.reopenedByInfo?.email && (
                                <p className="mb-0 text-muted small">
                                  <i className="fas fa-envelope me-1"></i>
                                  {ticket.reopenedByInfo.email}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {(ticket.assignedEmployeeInfo || ticket.assignedVendorInfo) && (
                        <div className="col-md-6 mb-4">
                          <label className="form-label text-muted mb-1" style={{ fontSize: '1rem' }}>Currently Assigned To</label>
                          <div className="mb-0 fs-6 text-dark">
                            {ticket.assignedEmployeeInfo && (
                              <div className="d-flex align-items-center gap-2">
                                <div>
                                  <strong>{ticket.assignedEmployeeInfo.fullName}</strong>
                                  <div className="small text-muted">
                                    {ticket.assignedEmployeeInfo.email}
                                    {ticket.assignedEmployeeInfo.phoneNumber && ` • ${ticket.assignedEmployeeInfo.phoneNumber}`}
                                  </div>
                                </div>
                                <span className="badge bg-info">Staff</span>
                              </div>
                            )}
                            {ticket.assignedVendorInfo && (
                              <div className="d-flex align-items-center gap-2">
                                <div>
                                  <strong>{ticket.assignedVendorInfo.name}</strong>
                                  <div className="small text-muted">
                                    {ticket.assignedVendorInfo.email}
                                    {ticket.assignedVendorInfo.contactInfo && ` • ${ticket.assignedVendorInfo.contactInfo}`}
                                  </div>
                                </div>
                                <span className="badge bg-warning text-dark">Vendor</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="col-md-6 mb-4">
                        <label className="form-label text-muted mb-1" style={{ fontSize: '1rem' }}>Assignment Status</label>
                        <div>
                          {ticket.assignedEmployeeInfo || ticket.assignedVendorInfo ? (
                            <span className="badge bg-success">Assigned</span>
                          ) : (
                            <span className="badge bg-secondary">Not Assigned</span>
                          )}
                        </div>
                      </div>

                      <div className="col-12 mb-4">
                        <label className="form-label text-muted mb-1" style={{ fontSize: '1rem' }}>Description</label>
                        <p className="mb-0 fs-6 text-dark" style={{ textAlign: 'justify', lineHeight: '1.5' }}>
                          {ticket.description}
                        </p>
                      </div>

                      <div className="col-12 mb-4">
                        <label className="form-label text-muted mb-2" style={{ fontSize: '1rem' }}>Attachments</label>
                        {ticket.status === 'under review' ? (
                          <div className="row g-3">
                            {/* Before Assessment Images */}
                            {ticket.images && (
                              <div className="col-12">
                                <h6 className="text-dark mb-3 border-bottom pb-2">Before Assessment</h6>
                                <div className="row g-3">
                                  {(Array.isArray(ticket.images) ? ticket.images : [ticket.images]).map((img, index) => (
                                    <div key={index} className="col-auto">
                                      <img
                                        src={img}
                                        alt={`Before Assessment ${index + 1}`}
                                        className="img-fluid border"
                                        style={{
                                          width: '280px',
                                          height: '200px',
                                          objectFit: 'cover',
                                          cursor: 'pointer'
                                        }}
                                        onClick={() => openFullScreen(img)}
                                      />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* After Assessment Images */}
                            {ticket.assessImages && (
                              <div className="col-12 mt-4">
                                <h6 className="text-dark mb-3 border-bottom pb-2">After Assessment</h6>
                                <div className="row g-3">
                                  {(Array.isArray(ticket.assessImages) ? ticket.assessImages : [ticket.assessImages]).map((img, index) => (
                                    <div key={index} className="col-auto">
                                      <img
                                        src={img}
                                        alt={`After Assessment ${index + 1}`}
                                        className="img-fluid border"
                                        style={{
                                          width: '280px',
                                          height: '200px',
                                          objectFit: 'cover',
                                          cursor: 'pointer'
                                        }}
                                        onClick={() => openFullScreen(img)}
                                      />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Cost Attachment */}
                            {ticket.costAttachment && (
                              <div className="col-12 mt-4">
                                {/* <h6 className="text-dark mb-3 border-bottom pb-2">Cost Documentation</h6>
                                <a
                                  href={ticket.costAttachment}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-outline-primary"
                                >
                                  <i className="fas fa-file-download me-2"></i>View Cost Attachment
                                </a> */}
                              </div>
                            )}
                          </div>
                        ) : ticket.images ? (
                          <div className="row g-3">
                            {(Array.isArray(ticket.images) ? ticket.images : [ticket.images]).map((img, index) => (
                              <div key={index} className="col-auto">
                                <img
                                  src={img}
                                  alt={`Attachment ${index + 1}`}
                                  className="img-fluid border"
                                  style={{
                                    width: '280px',
                                    height: '200px',
                                    objectFit: 'cover',
                                    cursor: 'pointer'
                                  }}
                                  onClick={() => openFullScreen(img)}
                                />
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-muted fs-7">No attachments available</p>
                        )}
                      </div>
                    </div>

                    {/* Review Information - Only show for reviewed tickets */}
                    {ticket.reviewed && (
                      <div className="col-12 mb-4">
                        <div className="card border">
                          <div className="card-header bg-light">
                            <h6 className="mb-0 text-dark">
                              <i className="fas fa-clipboard-check me-2"></i>Review Information
                            </h6>
                          </div>
                          <div className="card-body">
                            <div className="row">
                              {/* Needs Fix */}
                              <div className="col-md-6 mb-3">
                                <label className="form-label text-muted mb-1">Requires Fix</label>
                                <div className="d-flex align-items-center">
                                  {ticket.needsFix === 'yes' ? (
                                    <span className="px-3 py-1 rounded-pill bg-success bg-opacity-10 text-success fw-semibold">
                                      <i className="fas fa-check me-2"></i>Yes
                                    </span>
                                  ) : (
                                    <span className="px-3 py-1 rounded-pill bg-danger bg-opacity-10 text-danger fw-semibold">
                                      <i className="fas fa-times me-2"></i>No
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* Payer Information */}
                              {ticket.payerType && (
                                <div className="col-md-6 mb-3">
                                  <label className="form-label text-muted mb-1">Payment Responsibility</label>
                                  <div className="d-flex align-items-center">
                                    <span className="badge bg-secondary text-capitalize">{ticket.payerType}</span>
                                  </div>
                                </div>
                              )}

                              {/* Review Text */}
                              {ticket.reviewText && (
                                <div className="col-12 mb-3">
                                  <label className="form-label text-muted mb-1">Review Notes</label>
                                  <div className="p-3 bg-light rounded">
                                    <p className="mb-0 text-dark" style={{ textAlign: 'justify', lineHeight: '1.5' }}>
                                      {ticket.reviewText}
                                    </p>
                                  </div>
                                </div>
                              )}

                              {/* Services and Costs */}
                              {ticket.services && ticket.services.length > 0 && (
                                <div className="col-12 mb-3">
                                  <label className="form-label text-muted mb-2">Cost Breakdown</label>
                                  <div className="table-responsive">
                                    <table className="table table-sm table-bordered">
                                      <thead className="table-light">
                                        <tr>
                                          <th>Service Description</th>
                                          <th className="text-center">Qty</th>
                                          <th className="text-end">Unit Price</th>
                                          <th className="text-end">Amount</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {ticket.services.map((service, index) => (
                                          <tr key={index}>
                                            <td>{service.description}</td>
                                            <td className="text-center">{service.quantity}</td>
                                            <td className="text-end">KSh {service.unitPrice?.toLocaleString()}</td>
                                            <td className="text-end fw-semibold">KSh {service.amount?.toLocaleString()}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                      <tfoot className="table-light">
                                        <tr>
                                          <td colSpan="3" className="text-end fw-bold">Total Amount:</td>
                                          <td className="text-end fw-bold">KSh {ticket.totalAmount?.toLocaleString()}</td>
                                        </tr>
                                      </tfoot>
                                    </table>
                                  </div>
                                </div>
                              )}

                              {/* Cost Attachment */}
                              {ticket.costAttachment && (
                                <div className="col-12">
                                  <label className="form-label text-muted mb-1">Cost Documentation</label>
                                  <div>
                                    <a
                                      href={ticket.costAttachment}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="btn btn-outline-secondary btn-sm"
                                    >
                                      <i className="fas fa-file-download me-2"></i>View Cost Attachment
                                    </a>
                                  </div>
                                </div>
                              )}

                              <div className="text-end mt-4">
                                <button
                                  className="btn btn-primary px-4"
                                  onClick={handleApproveReview}
                                  disabled={ticket.approved}
                                >
                                  {ticket.approved ? 'Approved' : 'Approve'}
                                </button>
                              </div>

                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Full Screen Image Viewer */}
                    {isFullScreen && (
                      <div
                        className="bg-dark bg-opacity-90 position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
                        style={{ zIndex: 9999 }}
                        onClick={closeFullScreen}
                      >
                        <div className="w-90 h-90 d-flex justify-content-center align-items-center">
                          <img
                            src={fullScreenImage}
                            alt="Full-Screen Attachment"
                            className="img-fluid"
                            style={{
                              maxWidth: '90%',
                              maxHeight: '90%',
                              objectFit: 'contain'
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Assessment Assignment Card */}
                    <div className="card border mt-4">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                          <h5 className="card-title text-dark mb-0">Assign for Assessment</h5>
                          {(ticket.assignedEmployeeInfo || ticket.assignedVendorInfo) && (
                            <span className="badge bg-success">Currently Assigned</span>
                          )}
                        </div>

                        {!ticket.approvedBy && (ticket.assignedEmployeeInfo || ticket.assignedVendorInfo) && (
                          <div className="alert alert-info mb-4">
                            <i className="fas fa-info-circle me-2"></i>
                            This ticket is already assigned.
                          </div>
                        )}

                        <div className="row g-3">
                          <div className="col-md-6">
                            <label className="form-label text-dark">Assign To</label>

                            {ticket.approvedBy ? (
                              <div className="alert alert-info mb-0">
                                <i className="bi bi-info-circle me-2"></i>
                                This ticket has already been reviewed and cannot be reassigned.
                              </div>
                            ) : (
                              <>
                                <select
                                  className="form-select border"
                                  value={byWho || (ticket.assignedEmployeeInfo ? 'propertyManager' : ticket.assignedVendorInfo ? 'vendor' : '')}
                                  onChange={(e) => setByWho(e.target.value)}
                                  disabled={!!ticket.approvedBy || ticket.isAssigned}
                                >
                                  <option value="">Select assignee type</option>
                                  <option value="propertyManager">Property Manager</option>
                                  <option value="vendor">Vendor</option>
                                  <option value="self">Assign to Myself</option>
                                </select>
                                {ticket.approvedBy && (
                                  <div className="form-text text-info">
                                    <i className="bi bi-info-circle me-1"></i>
                                    Ticket already reviewed - assignment locked
                                  </div>
                                )}
                              </>
                            )}
                          </div>

                          {/* Staff Selection - Show only when propertyManager is selected AND not self-assigning */}
                          {byWho === "propertyManager" && (
                            <div className="col-md-6">
                              <label className="form-label text-dark">Select Staff Member</label>
                              <select
                                className="form-select border"
                                value={selectedEmployee || ticket.selectedEmployee || ""}
                                onChange={(e) => setSelectedEmployee(e.target.value)}
                              >
                                <option value="">Choose staff member</option>
                                {users.map((user) => (
                                  <option key={user._id} value={user._id}>
                                    {user.fullName}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}

                          {/* Vendor Selection */}
                          {byWho === "vendor" && (
                            <div className="col-md-6">
                              <label className="form-label text-dark">Select Vendor</label>
                              <select
                                className="form-select border"
                                value={selectedVendor?._id || ticket.selectedVendor || ""}
                                onChange={(e) => {
                                  const selectedVendorId = e.target.value;
                                  const vendor = serviceVendors.find((serviceVendor) => serviceVendor._id === selectedVendorId);
                                  setSelectedVendor(vendor);
                                }}
                              >
                                <option value="" disabled>Choose vendor</option>
                                {serviceVendors.map((serviceVendor) => (
                                  <option key={serviceVendor._id} value={serviceVendor._id}>
                                    {serviceVendor.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}

                          {/* Self Assignment Info */}
                          {byWho === "self" && (
                            <div className="col-md-12">
                              <div className="alert alert-warning">
                                <i className="fas fa-user me-2"></i>
                                You are assigning this ticket to yourself as the Property Manager.
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="text-end mt-4">
                          <button
                            className="btn btn-primary px-4"
                            onClick={handleAssignForAssessment}
                            disabled={!byWho ||
                              (byWho === "propertyManager" && !selectedEmployee && !ticket.selectedEmployee) ||
                              (byWho === "vendor" && !selectedVendor && !ticket.selectedVendor)}
                          >
                            {ticket.assignedEmployeeInfo || ticket.assignedVendorInfo ? 'Change Assignment' : 'Assign Assessment'}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Ticket Actions Section */}
                    <div className="card border mt-4">
                      <div className="card-body">
                        <h5 className="card-title text-dark mb-4">Ticket Actions</h5>

                        <div className="row g-3">
                          {/* Cancel Ticket - Show for non-cancelled tickets */}
                          {ticket.status !== 'cancelled' && (
                            <div className="col-md-6">
                              <div className="d-grid">
                                <button
                                  className="btn btn-outline-danger"
                                  onClick={() => setShowCancelModal(true)}
                                  disabled={ticket.status === 'resolved' || ticket.status === 'closed'}
                                >
                                  <i className="fas fa-times-circle me-2"></i>
                                  Cancel Ticket
                                </button>
                                <small className="text-muted mt-1">
                                  Cancel this ticket with a reason
                                </small>
                              </div>
                            </div>
                          )}

                          {/* Reopen Ticket - Show for cancelled, resolved, or closed tickets */}
                          {(ticket.status === 'cancelled' || ticket.status === 'resolved' || ticket.status === 'closed') && (
                            <div className="col-md-6">
                              <div className="d-grid">
                                <button
                                  className="btn btn-outline-success"
                                  onClick={() => setShowReopenModal(true)}
                                >
                                  <i className="fas fa-redo me-2"></i>
                                  Reopen Ticket
                                </button>
                                <small className="text-muted mt-1">
                                  Reopen this ticket with a reason
                                </small>
                              </div>
                            </div>
                          )}

                          {/* Regular Action Buttons */}
                          <div className="col-md-6">
                            {ticket.ticketType === 'Complaints' ? (
                              ticket.reviewed ? (
                                <div className="d-grid">
                                  <button className="btn btn-success" disabled>
                                    <i className="fas fa-check-circle me-2"></i> Review Completed
                                  </button>
                                </div>
                              ) : (
                                <div className="d-grid">
                                  <button
                                    className="btn btn-success"
                                    onClick={() => handleAction(ticket)}
                                  >
                                    <i className="fas fa-check-circle me-2"></i> Review Ticket
                                  </button>
                                </div>
                              )
                            ) : (
                              ticket.reviewed ? (
                                <div className="d-grid">
                                  <button className="btn btn-success" disabled>
                                    <i className="fas fa-check-circle me-2"></i> Review Completed
                                  </button>
                                </div>
                              ) : (
                                <div className="d-grid">
                                  <button
                                    className="btn btn-success"
                                    onClick={() => navigate(`/facility/ticket_management/start_review/${ticket._id}`, {
                                      state: { ticket: ticket, requester: ticket.requester, description: ticket.description },
                                    })}
                                    disabled={ticket.status !== 'assigned'}
                                  >
                                    <i className="fas fa-check-circle me-2"></i>
                                    {ticket.status === 'assigned' ? 'Start Review' : 'Awaiting Assignment'}
                                  </button>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Cancel Ticket Modal */}
              {showCancelModal && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
                  <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                      <div className="modal-header bg-danger text-white">
                        <h5 className="modal-title">
                          <i className="fas fa-exclamation-triangle me-2"></i>
                          Cancel Ticket
                        </h5>
                        <button type="button" className="btn-close btn-close-white" onClick={() => setShowCancelModal(false)}></button>
                      </div>
                      <div className="modal-body">
                        <p>Are you sure you want to cancel this ticket? Please provide a reason for cancellation.</p>
                        <div className="mb-3">
                          <label className="form-label">Cancellation Reason</label>
                          <textarea
                            className="form-control"
                            rows="4"
                            placeholder="Enter reason for cancellation..."
                            value={cancelReason}
                            onChange={(e) => setCancelReason(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={() => setShowCancelModal(false)}>
                          Close
                        </button>
                        <button
                          type="button"
                          className="btn btn-danger"
                          onClick={handleCancelTicket}
                          disabled={!cancelReason.trim()}
                        >
                          <i className="fas fa-times-circle me-2"></i>
                          Confirm Cancellation
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Reopen Ticket Modal */}
              {showReopenModal && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
                  <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                      <div className="modal-header bg-success text-white">
                        <h5 className="modal-title">
                          <i className="fas fa-redo me-2"></i>
                          Reopen Ticket
                        </h5>
                        <button type="button" className="btn-close btn-close-white" onClick={() => setShowReopenModal(false)}></button>
                      </div>
                      <div className="modal-body">
                        <p>Please provide a reason for reopening this ticket.</p>
                        <div className="mb-3">
                          <label className="form-label">Reopening Reason</label>
                          <textarea
                            className="form-control"
                            rows="4"
                            placeholder="Enter reason for reopening..."
                            value={reopenReason}
                            onChange={(e) => setReopenReason(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={() => setShowReopenModal(false)}>
                          Close
                        </button>
                        <button
                          type="button"
                          className="btn btn-success"
                          onClick={handleReopenTicket}
                          disabled={!reopenReason.trim()}
                        >
                          <i className="fas fa-redo me-2"></i>
                          Confirm Reopen
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>

      <Dialog
        header={`Submit Review`}
        style={{ width: '70vw' }}
        visible={isReviewDialogOpen}
        onHide={() => {
          if (!isReviewDialogOpen) return;
          setReviewDialogOpen(false);
        }}
      >
        <div className="row">
          <div className="col-md-12 mt-3">
            <div className="mb-4">
              <label className="form-label">Review</label>
              <textarea
                className="form-control"
                rows={5}
                placeholder="Leave a review here"
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
              ></textarea>
            </div>
            <div className="mt-4" style={{ float: "right" }}>
              <button className="btn btn-primary" onClick={() => handleSubmitReview(ticketId, reviewText)}>
                Submit
              </button>
            </div>
          </div>
        </div>
      </Dialog>
    </Layout >
  )
}

export default ViewTicket